import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { ScatterChart } from '@mui/x-charts/ScatterChart';
import chartColorPalette from 'assets/styles/chartColorPalette';
import MessageWrapper from 'pages/Ticket/components/MessageWrapper';
import type { FC } from 'react';
import type { GenerateChartToolArguments } from 'services/types/analysis';
import { NAVY } from 'styles/tokens';

interface Props {
  args: string;
  size?: 'small' | 'medium' | 'large';
}

const GenerateChartToolComponent: FC<Props> = ({ args, size = 'medium' }: Props) => {
  const argumentsObject = JSON.parse(args) as GenerateChartToolArguments;
  const title = `Gráfico ${argumentsObject.yAxisLabel} vs ${argumentsObject.xAxisLabel}`;
  const longestLabel = argumentsObject.data.reduce(
    (acc, data) => (data.label.length > acc ? data.label.length : acc),
    5
  );
  const barAndLineSeries = [
    {
      data: argumentsObject.data.map((data) => data.value),
      label: argumentsObject.valueLabel || argumentsObject.yAxisLabel,
      stack: '1',
    },
  ];
  const scatterSeries = [
    {
      data: argumentsObject.data.map((data) => ({ id: data.label, x: Number(data.label), y: data.value })),
      label: argumentsObject.valueLabel || argumentsObject.yAxisLabel,
    },
  ];
  if (argumentsObject.data.length > 0 && argumentsObject.data.some((item) => item.secondaryValue)) {
    barAndLineSeries.push({
      data: argumentsObject.data.map((data) => data.secondaryValue),
      label: argumentsObject.secondaryValueLabel || 'Secondary Value',
      stack: argumentsObject.chartType === 'bar' ? '1' : '2', // BarCharts should be stacked, but line charts should not
    });
    scatterSeries.push({
      data: argumentsObject.data.map((data) => ({ id: data.label, x: Number(data.label), y: data.secondaryValue })),
      label: argumentsObject.secondaryValueLabel || 'Secondary Value',
    });
  }
  let height = 400;
  if (size === 'small') {
    height = 300;
  } else if (size === 'large') {
    height = 500;
  }
  return (
    <MessageWrapper caption={title} align="left" fullWidth>
      <Box sx={{ width: '100%' }}>
        {argumentsObject.chartType === 'bar' && (
          <BarChart
            title={title}
            colors={chartColorPalette}
            series={barAndLineSeries}
            height={height}
            xAxis={[
              {
                data: argumentsObject.data.map((data) => data.label),
                scaleType: 'band',
                height: 'auto' as const,
                tickLabelStyle: longestLabel > 8 ? { angle: 45, textAnchor: 'start', fontSize: 12 } : undefined,
              },
            ]}
            yAxis={[
              {
                label: argumentsObject.yAxisLabel,
              },
            ]}
            margin={{ bottom: Math.min(20 + longestLabel * 6, 200), left: 70 }}
            sx={{
              [`.${axisClasses.left} .${axisClasses.label}`]: {
                transform: 'translate(-24px, 0)',
              },
            }}
          />
        )}
        {argumentsObject.chartType === 'line' && (
          <LineChart
            title={title}
            colors={chartColorPalette}
            series={barAndLineSeries}
            height={height}
            xAxis={[
              {
                data: argumentsObject.data.map((data) => data.label),
                scaleType: 'band',
                tickLabelStyle: longestLabel > 8 ? { angle: 45, textAnchor: 'start', fontSize: 12 } : undefined,
              },
            ]}
            yAxis={[
              {
                label: argumentsObject.yAxisLabel,
              },
            ]}
            margin={{ bottom: Math.min(20 + longestLabel * 6, 200), left: 70 }}
          />
        )}
        {argumentsObject.chartType === 'pie' && (
          <PieChart
            skipAnimation
            title={title}
            colors={chartColorPalette}
            series={[
              {
                data: argumentsObject.data.map((data) => ({ id: data.label, label: data.label, value: data.value })),
              },
            ]}
            height={((Math.floor(argumentsObject.data.length / 5) + 1) * height) / 2}
          />
        )}
        {argumentsObject.chartType === 'scatter' && (
          <ScatterChart
            title={title}
            colors={chartColorPalette}
            series={scatterSeries}
            height={height}
            xAxis={[
              {
                label: argumentsObject.xAxisLabel,
              },
            ]}
            yAxis={[
              {
                label: argumentsObject.yAxisLabel,
              },
            ]}
          />
        )}
        {['line', 'bar'].includes(argumentsObject.chartType) && (
          <Box
            sx={{
              justifyContent: 'center',
              display: 'flex',
            }}
          >
            <Typography variant="body1" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY }}>
              {argumentsObject.xAxisLabel}
            </Typography>
          </Box>
        )}
      </Box>
    </MessageWrapper>
  );
};

export default GenerateChartToolComponent;
